import { Tag } from "antd";
import { Autocomplete } from "components/UI";
import CreateTag from "components/apps/tags/create";
import {
  useMSScheduleTags,
  usePostMSScheduleTags,
} from "queries/meetingSchedule";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Multi-select tag untuk meeting schedule (master terpisah dari WA/contact tags).
 * Controlled via `value` (array id) + `onChange`. `canCreate` menampilkan tombol
 * buat tag baru (nama + warna) yang langsung di-POST ke master meetingScheduleTags.
 */
const MeetingTagSelect = ({
  value,
  onChange,
  canCreate = true,
  placeholder,
  ...selectProps
}) => {
  const { t } = useTranslation();
  const { data: tags = [] } = useMSScheduleTags();
  const createMutation = usePostMSScheduleTags();
  const [openCreate, setOpenCreate] = useState(false);

  const tagRender = useCallback(
    (innerProps) => {
      const { label, value: val, closable, onClose } = innerProps;
      const found = tags.find((tg) => tg.id === val);
      const onPreventMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      return (
        <Tag
          color={found?.color}
          onMouseDown={onPreventMouseDown}
          closable={closable}
          onClose={onClose}
          className="m-0.5 font-medium !text-black"
        >
          {label ?? found?.name}
        </Tag>
      );
    },
    [tags]
  );

  const handleCreate = useCallback(
    (formValues) => {
      createMutation.mutate(formValues, {
        onSuccess: (res) => {
          const newTag = res?.data?.data;
          setOpenCreate(false);
          if (newTag?.id != null) {
            onChange?.([...(value || []), newTag.id]);
          }
        },
      });
    },
    [createMutation, onChange, value]
  );

  return (
    <>
      <Autocomplete
        placeholder={placeholder ?? t("select_tag")}
        options={tags}
        fieldNames={{ label: "name", value: "id" }}
        mode="multiple"
        filterOption
        optionFilterProp="name"
        tagRender={tagRender}
        withExtra={canCreate}
        titleExtra={t("add_tag")}
        onClickExtra={() => setOpenCreate(true)}
        value={value}
        onChange={onChange}
        {...selectProps}
      />
      {openCreate && (
        <CreateTag
          open
          title={t("add_tag")}
          confirmLoading={createMutation.isPending}
          onSubmit={handleCreate}
          onCancel={() => setOpenCreate(false)}
        />
      )}
    </>
  );
};

export default MeetingTagSelect;
